import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { getImageUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { MapPin, Briefcase, Calendar, UserPlus, MessageSquare, Check, Clock, Crown, Eye, ShieldCheck, Activity, Zap, Timer, Dumbbell, Heart as HeartIcon, TrendingUp, Award, GraduationCap, MoreHorizontal, Flag, Ban } from "lucide-react";
import ParentalControlCard from "@/components/ParentalControlCard";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import RadarChart from "@/components/charts/RadarChart";
import ImageLightbox from "@/components/ImageLightbox";

const ergoFields = [
  { key: "sprint_speed", label: "Sprint Speed", unit: "km/h", icon: Zap, color: "#F85037", desc: "Top speed in km/h" },
  { key: "vo2_max", label: "VO2 Max", unit: "ml/kg/min", icon: HeartIcon, color: "#074572", desc: "Maximal oxygen uptake" },
  { key: "vertical_jump", label: "Vertical Jump", unit: "cm", icon: TrendingUp, color: "#10B981", desc: "Standing vertical jump height" },
  { key: "agility_time", label: "Agility", unit: "sec", icon: Timer, color: "#F59E0B", desc: "T-test agility time (lower is better)" },
  { key: "bench_press", label: "Bench Press", unit: "kg", icon: Dumbbell, color: "#8B5CF6", desc: "One-rep max bench press" },
  { key: "endurance_score", label: "Endurance", unit: "/100", icon: Activity, color: "#06B6D4", desc: "Beep test / Cooper test score" },
];

const UEFA_BADGES = {
  "UEFA C": { label: "UEFA C", color: "#10B981", bg: "#ECFDF5" },
  "UEFA B": { label: "UEFA B", color: "#074572", bg: "#EFF6FF" },
  "UEFA A": { label: "UEFA A", color: "#F59E0B", bg: "#FFFBEB" },
  "UEFA Pro": { label: "UEFA Pro", color: "#F85037", bg: "#FEF2F2" },
  "UEFA Elite Youth A": { label: "Elite Youth A", color: "#8B5CF6", bg: "#F5F3FF" },
};

export default function ProfilePage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [connStatus, setConnStatus] = useState("none");
  const [loading, setLoading] = useState(true);

  const profileId = id || user?._id || user?.id;
  const isOwn = !id || profileId === (user?._id || user?.id);
  const [blocked, setBlocked] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [reportDescription, setReportDescription] = useState("");
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [avatarOpen, setAvatarOpen] = useState(false);

  useEffect(() => {
    if (isOwn || !profileId) return;
    api.get(`/users/${profileId}/block-status`).then(r => setBlocked(!!r.data?.blocked)).catch(() => {});
  }, [profileId, isOwn]);

  const handleBlock = async () => {
    if (!window.confirm(`Block ${profile?.name}? Their posts will disappear from your feed and they won't appear in your search results.`)) return;
    try {
      await api.post(`/users/${profileId}/block`);
      toast.success("User blocked");
      setBlocked(true);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to block"); }
  };

  const handleUnblock = async () => {
    try {
      await api.post(`/users/${profileId}/unblock`);
      toast.success("User unblocked");
      setBlocked(false);
    } catch { toast.error("Failed to unblock"); }
  };

  const handleReportSubmit = async () => {
    if (!reportReason.trim()) { toast.error("Please pick a reason"); return; }
    setReportSubmitting(true);
    try {
      await api.post("/report", {
        content_type: "user",
        content_id: profileId,
        reason: reportReason,
        description: reportDescription,
      });
      toast.success("Report submitted. Our team will review it.");
      setReportOpen(false);
      setReportReason("");
      setReportDescription("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit report");
    } finally { setReportSubmitting(false); }
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/users/${profileId}`);
        setProfile(data);
        const postsRes = await api.get(`/posts?page=1&limit=10`);
        setPosts((postsRes.data.posts || []).filter(p => p.author_id === profileId));
        if (!isOwn) {
          const statusRes = await api.get(`/connections/status/${profileId}`);
          setConnStatus(statusRes.data.status);
        }
      } catch { toast.error("Failed to load profile"); }
      finally { setLoading(false); }
    };
    if (profileId) load();
  }, [profileId, isOwn]);

  const handleConnect = async () => {
    try {
      await api.post(`/connections/request/${profileId}`);
      setConnStatus("pending");
      toast.success("Connection request sent!");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  if (loading) return <div className="max-w-3xl mx-auto py-16 text-center text-zinc-400">Loading profile...</div>;
  if (!profile) return <div className="max-w-3xl mx-auto py-16 text-center text-zinc-400">Profile not found</div>;

  const initials = profile.name?.split(" ").map(n => n[0]).join("").toUpperCase() || "?";
  const avatarUrl = getImageUrl(profile.profile_image);
  const hasErgoData = ergoFields.some(f => profile[f.key] != null);
  const experience = Array.isArray(profile.experience) ? profile.experience : [];
  const certifications = Array.isArray(profile.certifications) ? profile.certifications : [];
  const verifiedCerts = Array.isArray(profile.verified_certifications) ? profile.verified_certifications : [];
  const hasExperience = experience.length > 0;
  const canShowCertTab = ["coach", "scout", "club"].includes(profile.role) || certifications.length > 0 || verifiedCerts.length > 0;
  const hasCertifications = certifications.length > 0 || verifiedCerts.length > 0;

  // Profile completeness score
  const completenessChecks = [
    !!profile.bio, !!profile.location, !!profile.position, !!profile.club,
    !!profile.profile_image, (profile.skills?.length || 0) > 0,
    hasErgoData || profile.role !== "athlete", hasExperience || verifiedCerts.length > 0,
  ];
  const completeness = Math.round((completenessChecks.filter(Boolean).length / completenessChecks.length) * 100);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-0 py-6 flex flex-col lg:flex-row lg:items-start gap-6" data-testid="profile-page">
      <div className="flex-1 min-w-0 max-w-3xl lg:mx-auto">
      {/* Cover */}
      <div className="h-40 bg-gradient-to-r from-[#074572] to-[#0a5a8f] rounded-xl relative mb-16">
        <div className="absolute -bottom-12 left-6">
          <button onClick={() => avatarUrl && setAvatarOpen(true)} className={avatarUrl ? "cursor-pointer rounded-full" : "cursor-default"} data-testid="profile-avatar-button">
            <Avatar className="h-24 w-24 border-4 border-white shadow-lg">
              {avatarUrl ? <AvatarImage src={avatarUrl} alt={profile.name} /> : null}
              <AvatarFallback className="bg-[#074572] text-white text-2xl font-semibold">{initials}</AvatarFallback>
            </Avatar>
          </button>
          <ImageLightbox src={avatarOpen ? avatarUrl : null} alt={profile.name} onClose={() => setAvatarOpen(false)} />
        </div>
      </div>

      {/* Info */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-medium text-zinc-900" style={{ fontFamily: 'Outfit' }}>{profile.name}</h1>
            <Badge className="bg-[#074572]/5 text-[#074572] capitalize">{profile.role}</Badge>
            {profile.is_premium && <Crown size={16} className="text-amber-500" />}
            {profile.ergo_verified && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger><ShieldCheck size={18} className="text-[#074572]" data-testid="ergo-verified-badge" /></TooltipTrigger>
                  <TooltipContent><p className="text-xs">Ergometric stats verified by platform</p></TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            {profile.coaching_license && UEFA_BADGES[profile.coaching_license] && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ backgroundColor: UEFA_BADGES[profile.coaching_license].bg, color: UEFA_BADGES[profile.coaching_license].color }} data-testid="uefa-badge">
                      <GraduationCap size={10} />{UEFA_BADGES[profile.coaching_license].label}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent><p className="text-xs">{profile.coaching_license} Licensed Coach - Verified by SportsUnity</p></TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            {profile.position && <p className="text-sm text-zinc-500">{profile.position}</p>}
            {profile.open_to_opportunities && (
              <Badge className="text-[10px] bg-green-50 text-green-700 border-green-200">
                <Eye size={8} className="mr-1" /> Open to Opportunities
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-4 mt-2 text-sm text-zinc-400 flex-wrap">
            {profile.location && <span className="flex items-center gap-1"><MapPin size={14} />{profile.location}</span>}
            {profile.club && <span className="flex items-center gap-1"><Briefcase size={14} />{profile.club}</span>}
            {profile.experience_years && <span className="flex items-center gap-1"><Calendar size={14} />{profile.experience_years} years exp</span>}
          </div>
        </div>
        <div className="flex gap-2">
          {isOwn ? (
            <Button variant="outline" className="border-[#E6E6E6] text-[#074572]" onClick={() => navigate("/settings")} data-testid="profile-edit-btn">Edit Profile</Button>
          ) : (
            <>
              {connStatus === "none" && <Button className="bg-[#F85037] hover:bg-[#e04530] text-white gap-2" onClick={handleConnect} data-testid="profile-connect-btn"><UserPlus size={16} /> Connect</Button>}
              {connStatus === "pending" && <Button variant="outline" disabled className="border-[#E6E6E6] gap-2"><Clock size={16} /> Pending</Button>}
              {connStatus === "accepted" && <Button variant="outline" className="border-green-200 text-green-600 gap-2"><Check size={16} /> Connected</Button>}
              <Button variant="outline" className="border-[#E6E6E6] text-[#074572] gap-2" onClick={() => navigate(`/messages/${profileId}`)} data-testid="profile-message-btn"><MessageSquare size={16} /> Message</Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon" className="border-[#E6E6E6] text-zinc-600" data-testid="profile-more-menu">
                    <MoreHorizontal size={16} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-44">
                  <DropdownMenuItem onClick={() => setReportOpen(true)} data-testid="profile-report-btn">
                    <Flag size={14} className="mr-2 text-[#F85037]" /> Report User
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  {blocked ? (
                    <DropdownMenuItem onClick={handleUnblock} data-testid="profile-unblock-btn">
                      <Ban size={14} className="mr-2 text-emerald-600" /> Unblock User
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuItem onClick={handleBlock} className="text-[#F85037]" data-testid="profile-block-btn">
                      <Ban size={14} className="mr-2" /> Block User
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          )}
        </div>
      </div>

      {/* Report Dialog */}
      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
              <Flag size={16} className="text-[#F85037]" /> Report {profile?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-zinc-500">
              We&apos;ll email our safety team at <span className="font-medium">info@sportsunity.gr</span> to review this report.
            </p>
            <div>
              <label className="text-xs font-medium text-zinc-600 mb-1 block">Reason</label>
              <select
                value={reportReason}
                onChange={(e) => setReportReason(e.target.value)}
                className="w-full h-9 px-3 border border-[#E6E6E6] rounded-md text-sm bg-white"
                data-testid="report-reason-select"
              >
                <option value="">Select a reason...</option>
                <option value="spam">Spam or promotional content</option>
                <option value="harassment">Harassment or bullying</option>
                <option value="fake_profile">Fake profile / impersonation</option>
                <option value="inappropriate">Inappropriate content</option>
                <option value="scam">Scam or fraud</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-zinc-600 mb-1 block">Additional details (optional)</label>
              <Textarea
                value={reportDescription}
                onChange={(e) => setReportDescription(e.target.value)}
                placeholder="Provide any context that helps us review this report..."
                className="border-[#E6E6E6] text-sm min-h-[80px]"
                maxLength={500}
                data-testid="report-description"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 border-t border-[#E6E6E6] pt-3">
            <Button variant="outline" onClick={() => setReportOpen(false)} className="border-[#E6E6E6] h-8 text-xs">Cancel</Button>
            <Button
              onClick={handleReportSubmit}
              disabled={reportSubmitting || !reportReason}
              className="bg-[#F85037] hover:bg-[#e04530] text-white h-8 text-xs"
              data-testid="report-submit-btn"
            >
              {reportSubmitting ? "Sending..." : "Submit Report"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-[#E6E6E6] rounded-md p-4 text-center">
          <p className="text-xl font-semibold text-[#074572]" style={{ fontFamily: 'Outfit' }}>{profile.connection_count || 0}</p>
          <p className="text-xs text-zinc-500 mt-0.5">Connections</p>
        </div>
        <div className="bg-white border border-[#E6E6E6] rounded-md p-4 text-center">
          <p className="text-xl font-semibold text-[#074572]" style={{ fontFamily: 'Outfit' }}>{profile.experience_years ? `${profile.experience_years}yr` : "-"}</p>
          <p className="text-xs text-zinc-500 mt-0.5">Experience</p>
        </div>
        {isOwn ? (
          <div className="bg-white border border-[#E6E6E6] rounded-md p-4 text-center" data-testid="profile-completeness">
            <p className="text-xl font-semibold text-[#F85037]" style={{ fontFamily: 'Outfit' }}>{completeness}%</p>
            <p className="text-xs text-zinc-500 mt-0.5">Profile Strength</p>
          </div>
        ) : (
          <div className="bg-white border border-[#E6E6E6] rounded-md p-4 text-center">
            <p className="text-xl font-semibold text-[#074572]" style={{ fontFamily: 'Outfit' }}>{verifiedCerts.length}</p>
            <p className="text-xs text-zinc-500 mt-0.5">Verified Certs</p>
          </div>
        )}
      </div>

      <Separator className="bg-[#E6E6E6] mb-6" />

      <Tabs defaultValue="about" className="w-full">
        <TabsList className="bg-transparent border-b border-[#E6E6E6] rounded-none w-full justify-start gap-6 px-0 h-auto pb-0">
          <TabsTrigger value="about" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#074572] data-[state=active]:text-[#074572] data-[state=active]:shadow-none bg-transparent px-1 pb-3 text-sm" data-testid="profile-tab-about">About</TabsTrigger>
          {hasErgoData && (
            <TabsTrigger value="ergometrics" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#F85037] data-[state=active]:text-[#F85037] data-[state=active]:shadow-none bg-transparent px-1 pb-3 text-sm gap-1.5 font-semibold" data-testid="profile-tab-ergometrics">
              <Activity size={14} className="text-[#F85037]" />
              <span>Ergometrics</span>
              {profile.ergo_verified && <ShieldCheck size={12} className="text-[#074572]" />}
            </TabsTrigger>
          )}
          {hasExperience && (
            <TabsTrigger value="experience" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#074572] data-[state=active]:text-[#074572] data-[state=active]:shadow-none bg-transparent px-1 pb-3 text-sm gap-1.5" data-testid="profile-tab-experience">
              <Briefcase size={14} />
              <span>Experience</span>
            </TabsTrigger>
          )}
          {canShowCertTab && (
            <TabsTrigger value="certifications" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#074572] data-[state=active]:text-[#074572] data-[state=active]:shadow-none bg-transparent px-1 pb-3 text-sm gap-1.5" data-testid="profile-tab-certifications">
              <Award size={14} />
              <span>Certifications</span>
              {verifiedCerts.length > 0 && <ShieldCheck size={12} className="text-[#074572]" />}
            </TabsTrigger>
          )}
          <TabsTrigger value="posts" className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#074572] data-[state=active]:text-[#074572] data-[state=active]:shadow-none bg-transparent px-1 pb-3 text-sm" data-testid="profile-tab-posts">Posts</TabsTrigger>
        </TabsList>

        <TabsContent value="about" className="mt-6">
          {profile.bio && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-zinc-900 mb-2 uppercase tracking-wide" style={{ fontFamily: 'Outfit' }}>Bio</h3>
              <p className="text-sm text-zinc-600 leading-relaxed">{profile.bio}</p>
            </div>
          )}
          {profile.skills?.length > 0 && (
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-zinc-900 mb-2 uppercase tracking-wide" style={{ fontFamily: 'Outfit' }}>Skills</h3>
              <div className="flex flex-wrap gap-2">
                {profile.skills.map(s => <span key={s} className="text-xs px-3 py-1 bg-[#074572]/5 text-[#074572] rounded-full font-medium">{s}</span>)}
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            {profile.date_of_birth && <InfoItem label="Date of Birth" value={new Date(profile.date_of_birth).toLocaleDateString()} />}
            {profile.preferred_foot && <InfoItem label="Preferred Foot" value={profile.preferred_foot} />}
            {profile.height && <InfoItem label="Height" value={profile.height} />}
            {profile.weight && <InfoItem label="Weight" value={profile.weight} />}
            {profile.age != null && <InfoItem label="Age" value={profile.age} />}
            {profile.role === "coach" && profile.coaching_role && <InfoItem label="Coaching Role" value={profile.coaching_role} />}
            {profile.role === "scout" && profile.scouting_region && <InfoItem label="Scouting Region" value={profile.scouting_region} />}
            {profile.role === "scout" && profile.scouting_focus && <InfoItem label="Main Focus" value={profile.scouting_focus} />}
          </div>

          {profile.role === "parent" && (profile.child_name || profile.child_club) && (
            <div className="mt-6 p-4 bg-[#074572]/5 border border-[#074572]/10 rounded-lg" data-testid="parent-child-info">
              <h3 className="text-sm font-semibold text-zinc-900 mb-3 uppercase tracking-wide" style={{ fontFamily: 'Outfit' }}>My Child</h3>
              <div className="grid grid-cols-2 gap-3">
                {profile.child_name && <InfoItem label="Name" value={profile.child_name} />}
                {profile.child_date_of_birth && <InfoItem label="Date of Birth" value={new Date(profile.child_date_of_birth).toLocaleDateString()} />}
                {profile.child_club && <InfoItem label="Academy / Club" value={profile.child_club} />}
                {profile.child_user_id && (
                  <div className="bg-zinc-50 border border-zinc-100 rounded-md p-3">
                    <p className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider">Linked Athlete</p>
                    <Link to={`/profile/${profile.child_user_id}`} className="text-sm text-[#074572] font-medium mt-0.5 block hover:underline">View profile →</Link>
                  </div>
                )}
              </div>
            </div>
          )}
        </TabsContent>

        {/* Ergometrics Tab */}
        {hasErgoData && (
          <TabsContent value="ergometrics" className="mt-6" data-testid="ergo-section">
            <div className="flex items-center gap-2 mb-4">
              <Activity size={16} className="text-[#074572]" />
              <h3 className="text-sm font-semibold text-zinc-900 uppercase tracking-wide" style={{ fontFamily: 'Outfit' }}>Ergometric Stats</h3>
              {profile.ergo_verified && (
                <Badge className="text-[10px] bg-[#074572]/5 text-[#074572] gap-1">
                  <ShieldCheck size={10} /> Verified
                </Badge>
              )}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {ergoFields.map(f => {
                const val = profile[f.key];
                if (val == null) return null;
                const Icon = f.icon;
                return (
                  <div key={f.key} className="bg-white border border-[#E6E6E6] rounded-lg p-4 hover:shadow-sm transition-shadow" data-testid={`ergo-${f.key}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{ backgroundColor: `${f.color}10` }}>
                        <Icon size={16} style={{ color: f.color }} />
                      </div>
                      <div>
                        <p className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider">{f.label}</p>
                      </div>
                    </div>
                    <p className="text-2xl font-bold text-zinc-900" style={{ fontFamily: 'Outfit' }}>
                      {val}<span className="text-xs font-normal text-zinc-400 ml-1">{f.unit}</span>
                    </p>
                    <p className="text-[10px] text-zinc-400 mt-1">{f.desc}</p>
                  </div>
                );
              })}
            </div>
            {!profile.ergo_verified && (
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-md flex items-center gap-2">
                <ShieldCheck size={14} className="text-amber-600" />
                <p className="text-xs text-amber-700">These stats are self-reported and not yet verified by the platform.</p>
              </div>
            )}
          </TabsContent>
        )}

        {/* Experience Tab */}
        {hasExperience && (
          <TabsContent value="experience" className="mt-6" data-testid="experience-section">
            <div className="flex items-center gap-2 mb-4">
              <Briefcase size={16} className="text-[#074572]" />
              <h3 className="text-sm font-semibold text-zinc-900 uppercase tracking-wide" style={{ fontFamily: 'Outfit' }}>Career & Experience</h3>
            </div>
            <div className="space-y-3">
              {experience.map((exp, idx) => (
                <div key={`${exp.title || "x"}-${exp.organization || "y"}-${idx}`} className="bg-white border border-[#E6E6E6] rounded-lg p-4 flex gap-3" data-testid={`experience-item-${idx}`}>
                  <div className="w-10 h-10 rounded-md bg-[#074572]/5 flex items-center justify-center shrink-0">
                    <Briefcase size={18} className="text-[#074572]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-zinc-900">{exp.title}</p>
                    <p className="text-xs text-zinc-600 mt-0.5">{exp.organization}</p>
                    <p className="text-[11px] text-zinc-400 mt-1">
                      {exp.start_date}{exp.current ? " — Present" : exp.end_date ? ` — ${exp.end_date}` : ""}
                    </p>
                    {exp.description && <p className="text-xs text-zinc-600 mt-2 leading-relaxed">{exp.description}</p>}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        )}

        {/* Certifications Tab */}
        {canShowCertTab && (
          <TabsContent value="certifications" className="mt-6" data-testid="certifications-section">
            <div className="flex items-center gap-2 mb-4">
              <Award size={16} className="text-[#074572]" />
              <h3 className="text-sm font-semibold text-zinc-900 uppercase tracking-wide" style={{ fontFamily: 'Outfit' }}>Certifications & Diplomas</h3>
            </div>
            {!hasCertifications ? (
              <p className="text-center py-8 text-zinc-400 text-sm">No certifications added yet</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {verifiedCerts.map((c, idx) => {
                  const badge = UEFA_BADGES[c.cert_level];
                  return (
                    <div key={`v-${idx}`} className="bg-white border-2 border-[#074572]/20 rounded-lg p-4" data-testid={`verified-cert-${idx}`}>
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-md flex items-center justify-center shrink-0" style={{ backgroundColor: badge?.bg || "#EFF6FF" }}>
                          <GraduationCap size={18} style={{ color: badge?.color || "#074572" }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <p className="text-sm font-semibold text-zinc-900">{c.cert_name}</p>
                            <Badge className="text-[9px] bg-[#074572]/5 text-[#074572] gap-1 px-1.5 py-0">
                              <ShieldCheck size={9} /> Verified
                            </Badge>
                          </div>
                          {c.cert_level && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 mt-1.5 rounded-full text-[10px] font-bold" style={{ backgroundColor: badge?.bg || "#EFF6FF", color: badge?.color || "#074572" }}>
                              <GraduationCap size={9} />{badge?.label || c.cert_level}
                            </span>
                          )}
                          {c.approved_at && <p className="text-[10px] text-zinc-400 mt-1.5">Approved {new Date(c.approved_at).toLocaleDateString()}</p>}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {certifications.filter(c => !verifiedCerts.some(v => v.cert_name === c.cert_name)).map((c, idx) => (
                  <div key={`u-${idx}`} className="bg-white border border-dashed border-[#E6E6E6] rounded-lg p-4" data-testid={`unverified-cert-${idx}`}>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-md bg-zinc-50 flex items-center justify-center shrink-0">
                        <Award size={18} className="text-zinc-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-zinc-900">{c.cert_name || "Certification"}</p>
                        {c.cert_level && <p className="text-xs text-zinc-500 mt-0.5">{c.cert_level}</p>}
                        {c.issuer && <p className="text-[11px] text-zinc-400 mt-0.5">{c.issuer}{c.year ? ` · ${c.year}` : ""}</p>}
                        <Badge className="text-[9px] mt-1.5 bg-amber-50 text-amber-700 border-amber-200">Self-reported</Badge>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        )}

        <TabsContent value="posts" className="mt-6">
          {posts.length === 0 ? (
            <p className="text-center py-8 text-zinc-400 text-sm">No posts yet</p>
          ) : (
            <div className="space-y-4">
              {posts.map(p => (
                <div key={p.id} className="bg-white border border-[#E6E6E6] rounded-md p-4">
                  <p className="text-sm text-zinc-700">{p.content}</p>
                  {p.image_url && <img src={p.image_url.startsWith("http") ? p.image_url : getImageUrl(p.image_url)} alt="" className="mt-3 rounded-md w-full max-h-60 object-cover" />}
                  <p className="text-xs text-zinc-400 mt-2">{new Date(p.created_at).toLocaleDateString()}</p>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
      </div>

      {/* Right Rail */}
      <ProfileRightRail profile={profile} isOwn={isOwn} />
    </div>
  );
}

function ProfileRightRail({ profile, isOwn }) {
  const isPremiumAthlete = profile.role === "athlete" && profile.is_premium;
  const showPremiumStats = isPremiumAthlete && ergoFields.some(f => profile[f.key] != null);

  return (
    <aside className="w-full lg:w-72 shrink-0 space-y-4" data-testid="profile-right-rail">
      {isOwn && <ParentalControlCard />}
      {showPremiumStats ? (
        <PremiumStatsCard profile={profile} />
      ) : (
        <StandardRightRail profile={profile} />
      )}
    </aside>
  );
}

function PremiumStatsCard({ profile }) {
  const topStats = ergoFields.filter(f => profile[f.key] != null).slice(0, 4);
  const [peer, setPeer] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.get(`/scout/position-averages${profile.position ? `?position=${encodeURIComponent(profile.position)}` : ""}`);
        if (active) setPeer(data);
      } catch (err) {
        // radar will fall back to default ranges
        console.warn("position-averages fetch failed:", err);
      }
    })();
    return () => { active = false; };
  }, [profile.position]);

  // Build scale (min/max) for radar normalization. Use peer.min/max with sensible defaults.
  const defaults = {
    sprint_speed: { min: 20, max: 38 },
    vo2_max: { min: 35, max: 75 },
    vertical_jump: { min: 30, max: 90 },
    agility_time: { min: 3.5, max: 6.5 },
    bench_press: { min: 30, max: 150 },
    endurance_score: { min: 0, max: 100 },
  };
  const scale = {};
  ergoFields.forEach(f => {
    const peerStat = peer?.stats?.[f.key];
    const d = defaults[f.key];
    if (peerStat && peerStat.n > 1) {
      const spread = (peerStat.max - peerStat.min) || (peerStat.avg * 0.2) || 1;
      scale[f.key] = { min: Math.max(0, peerStat.min - spread * 0.2), max: peerStat.max + spread * 0.2 };
    } else {
      scale[f.key] = d;
    }
  });

  const athleteValues = {};
  const peerValues = {};
  ergoFields.forEach(f => {
    athleteValues[f.key] = profile[f.key];
    peerValues[f.key] = peer?.stats?.[f.key]?.avg;
  });

  const hasAllRadarData = ergoFields.every(f => profile[f.key] != null);
  const peerSampleSize = peer?.sample_size || 0;

  return (
    <div className="relative overflow-hidden rounded-xl shadow-lg" data-testid="premium-stats-card">
      {/* Gradient bg */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#074572] via-[#0a5a8f] to-[#053a60]" />
      <div className="absolute top-0 right-0 w-32 h-32 bg-[#F85037]/20 rounded-full blur-3xl -translate-y-12 translate-x-12" />
      <div className="absolute bottom-0 left-0 w-24 h-24 bg-amber-400/10 rounded-full blur-2xl translate-y-8 -translate-x-8" />

      <div className="relative p-5 text-white">
        <div className="flex items-center gap-2 mb-1">
          <Crown size={14} className="text-amber-400" />
          <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-amber-400">Premium Athlete</span>
        </div>
        <h3 className="text-lg font-semibold mb-1" style={{ fontFamily: 'Outfit' }}>Performance Data</h3>
        <p className="text-[11px] text-white/60 mb-4">Verified ergometric stats</p>

        {profile.ergo_verified && (
          <div className="inline-flex items-center gap-1 px-2 py-0.5 bg-white/15 rounded-full mb-4 backdrop-blur-sm">
            <ShieldCheck size={10} className="text-emerald-300" />
            <span className="text-[10px] font-medium">Verified</span>
          </div>
        )}

        {/* Radar Chart */}
        {hasAllRadarData && (
          <div className="flex justify-center my-3 -mx-2" data-testid="profile-radar-chart">
            <RadarChart
              size={240}
              axes={[
                { key: "sprint_speed", label: "SPD", lowerBetter: false },
                { key: "vo2_max", label: "VO2", lowerBetter: false },
                { key: "vertical_jump", label: "JMP", lowerBetter: false },
                { key: "agility_time", label: "AGI", lowerBetter: true },
                { key: "bench_press", label: "BNC", lowerBetter: false },
                { key: "endurance_score", label: "END", lowerBetter: false },
              ]}
              datasets={[{ label: profile.name?.split(" ")[0] || "You", color: "#F85037", values: athleteValues }]}
              reference={peerSampleSize > 1 ? { label: `Position avg (n=${peerSampleSize})`, color: "#fbbf24", values: peerValues } : null}
              scale={scale}
              labelColor="rgba(255,255,255,0.85)"
              axisColor="rgba(255,255,255,0.18)"
            />
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          {topStats.map(f => {
            const Icon = f.icon;
            return (
              <div key={f.key} className="bg-white/10 backdrop-blur-sm border border-white/15 rounded-lg p-3" data-testid={`premium-stat-${f.key}`}>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Icon size={11} className="text-[#F85037]" />
                  <p className="text-[9px] uppercase tracking-wider text-white/60 font-semibold leading-none">{f.label}</p>
                </div>
                <p className="text-xl font-bold leading-none" style={{ fontFamily: 'Outfit' }}>
                  {profile[f.key]}
                  <span className="text-[10px] text-white/50 font-normal ml-1">{f.unit}</span>
                </p>
              </div>
            );
          })}
        </div>

        {ergoFields.filter(f => profile[f.key] != null).length > 4 && (
          <p className="text-[10px] text-white/50 mt-3 text-center">+ {ergoFields.filter(f => profile[f.key] != null).length - 4} more in Ergometrics tab</p>
        )}

        <div className="grid grid-cols-2 gap-2 mt-4 pt-4 border-t border-white/10">
          {profile.position && <RailMiniStat label="Position" value={profile.position} />}
          {profile.preferred_foot && <RailMiniStat label="Foot" value={profile.preferred_foot} />}
          {profile.height && <RailMiniStat label="Height" value={profile.height} />}
          {profile.weight && <RailMiniStat label="Weight" value={profile.weight} />}
        </div>
      </div>
    </div>
  );
}

function RailMiniStat({ label, value }) {
  return (
    <div>
      <p className="text-[9px] uppercase tracking-wider text-white/50 font-semibold">{label}</p>
      <p className="text-xs font-semibold text-white mt-0.5">{value}</p>
    </div>
  );
}

function StandardRightRail({ profile }) {
  return (
    <>
      {/* Recommendations placeholder */}
      <div className="bg-white border border-[#E6E6E6] rounded-lg p-4" data-testid="rail-recommendations">
        <h3 className="text-xs font-semibold text-zinc-900 uppercase tracking-wide mb-2" style={{ fontFamily: 'Outfit' }}>Recommendations</h3>
        <p className="text-xs text-zinc-500 leading-relaxed">Endorsements and recommendations from coaches and teammates will appear here.</p>
        <p className="text-[10px] text-zinc-400 mt-2 italic">Coming soon</p>
      </div>

      {/* Suggested Events */}
      <Link to="/events" className="block bg-white border border-[#E6E6E6] rounded-lg p-4 hover:border-[#074572]/30 transition-colors" data-testid="rail-events">
        <div className="flex items-center gap-2 mb-2">
          <Calendar size={14} className="text-[#074572]" />
          <h3 className="text-xs font-semibold text-zinc-900 uppercase tracking-wide" style={{ fontFamily: 'Outfit' }}>Upcoming Events</h3>
        </div>
        <p className="text-xs text-zinc-600">Find scouting days, trials and workshops near you.</p>
        <p className="text-[11px] text-[#074572] font-medium mt-2">Browse events →</p>
      </Link>

      {/* Upgrade nudge for non-premium athletes */}
      {profile.role === "athlete" && !profile.is_premium && (
        <div className="relative overflow-hidden rounded-lg border border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 p-4" data-testid="rail-upgrade-promo">
          <div className="absolute top-0 right-0 w-20 h-20 bg-[#F85037]/10 rounded-full blur-2xl" />
          <div className="relative">
            <div className="flex items-center gap-1.5 mb-2">
              <Crown size={14} className="text-amber-500" />
              <span className="text-[10px] uppercase tracking-wider font-bold text-[#F85037]">Premium Boost</span>
            </div>
            <p className="text-sm font-semibold text-zinc-900" style={{ fontFamily: 'Outfit' }}>Stand out to scouts</p>
            <p className="text-[11px] text-zinc-600 mt-1 leading-relaxed">Premium athletes get a highlighted stats card visible on every profile view.</p>
            <Link to="/premium" className="inline-block mt-3 px-3 py-1.5 bg-[#F85037] hover:bg-[#e04530] text-white text-[11px] font-semibold rounded-full transition-colors">
              Upgrade now
            </Link>
          </div>
        </div>
      )}

      {/* Promoted */}
      <div className="bg-zinc-50 border border-dashed border-[#E6E6E6] rounded-lg p-4 text-center" data-testid="rail-promoted">
        <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-semibold">Promoted</p>
        <p className="text-xs text-zinc-500 mt-1.5">Sponsored content & partner ads will appear here.</p>
      </div>
    </>
  );
}

function InfoItem({ label, value }) {
  return (
    <div className="bg-zinc-50 border border-zinc-100 rounded-md p-3">
      <p className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider">{label}</p>
      <p className="text-sm text-zinc-800 font-medium mt-0.5">{value}</p>
    </div>
  );
}
